import { type RefObject, useEffect, useId } from "react";

import { useLiveChatStore } from "@stores/live_chat";

/** The chat button's corner: 16px + 56px on phones, 40px + 56px from md. */
const CORNER = 96;

/**
 * Something at the bottom of the screen reports how much room it takes
 * there, so the chat button moves above it instead of covering it: the plan
 * page's order bar (phones and tablets), the checkout's sticky Pay bar
 * (phones), the cookie banner. It counts only while it sits within 32px of
 * the bottom edge and reaches the button's corner (the end side; the start
 * side in RTL); hidden (lg:hidden) or scrolled away, it reports 0.
 */
export const useChatBottomInset = (
  ref: RefObject<HTMLElement | null>,
  enabled = true,
): void => {
  const key = useId();
  const setBottomInset = useLiveChatStore(state => state.setBottomInset);
  useEffect(() => {
    const el = ref.current;
    if (!enabled || el === null) return;
    let frame = 0;
    const measure = (): void => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const height = window.innerHeight;
        const isNearBottom =
          rect.height > 0 && rect.top < height && rect.bottom >= height - 32;
        const reachesCorner =
          document.documentElement.dir === "rtl"
            ? rect.left <= CORNER
            : rect.right >= window.innerWidth - CORNER;
        setBottomInset(
          key,
          isNearBottom && reachesCorner ? Math.round(height - rect.top) : 0,
        );
      });
    };
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    measure();
    // Once more after an entrance animation (the cookie banner slides up).
    const settle = window.setTimeout(measure, 400);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settle);
      observer.disconnect();
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      setBottomInset(key, 0);
    };
  }, [ref, enabled, key, setBottomInset]);
};
